var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { sumBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { MatcherName, registerLocallyHandledErrorMatcher, singleErrorMatcher, } from 'api/locallyHandledErrors';
import useDebouncedCallbackPerKey from 'hooks/useDebounceCallbackPerKey';
import CheckIcon from 'images/v5/icons/check.svg';
import ChevronDownSvg from 'images/v5/icons/chevron-down.svg';
import EditSvg from 'images/v5/icons/edit-02.svg';
import MinusIcon from 'images/v5/icons/minus.svg';
import PlusSvg from 'images/v5/icons/plus.svg';
import TrashSvg from 'images/v5/icons/trash-03.svg';
import Button from 'layout/atoms/Button/Button';
import Caption from 'layout/atoms/Caption';
import Checkbox from 'layout/atoms/Checkbox/Checkbox';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Header from 'layout/atoms/Header/Header';
import InputNumber from 'layout/atoms/InputNumber';
import LinkButton from 'layout/atoms/LinkButton';
import Loader from 'layout/atoms/Loader/Loader';
import TableGrid, { Body, Cell, Footer, Head, Row } from 'layout/atoms/TableGrid';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import Grid from 'layout/molecules/Grid/Grid';
import { openModal } from 'store/layoutSlice';
import { fetchSpecificationProblems, selectStepValidationRequestId, } from 'store/specificationDetailsSlice';
import { activateSize, changeSizeStandard, deactivateSize, fetchSizings, fetchSpecificationProductionQuantities, removeModelVariants, selectProductionQuantitiesItem, selectProductionQuantitiesRefetchFlags, selectProductionQuantitiesStatus, updateModelVariants, updateStoreModelVariants, } from 'store/specificationProductionQuantitiesSlice';
import { addToast } from 'store/toastsSlice';
import exceptionMessageToKey from 'utils/exceptionMessage';
registerLocallyHandledErrorMatcher(MatcherName.RemoveModelColorsException, singleErrorMatcher({ message: 'FashionappException', operationName: 'RemoveModelColors' }));
function ProductionQuantities() {
    var _a;
    const { t } = useTranslation(['specification', 'common']);
    const dispatch = useDispatch();
    const specificationDetails = useSelector((state) => state.specificationDetails.item);
    const sizings = useSelector((state) => state.specificationProductionQuantities.sizings.items);
    const productionQuantitiesStatus = useSelector(selectProductionQuantitiesStatus);
    const productionQuantitiesRefetchFlags = useSelector(selectProductionQuantitiesRefetchFlags);
    const productionQuantitiesItem = useSelector(selectProductionQuantitiesItem);
    const sizeStandard = (_a = productionQuantitiesItem === null || productionQuantitiesItem === void 0 ? void 0 : productionQuantitiesItem.sizeStandard) !== null && _a !== void 0 ? _a : null;
    const sizes = productionQuantitiesItem === null || productionQuantitiesItem === void 0 ? void 0 : productionQuantitiesItem.sizes;
    const colors = productionQuantitiesItem === null || productionQuantitiesItem === void 0 ? void 0 : productionQuantitiesItem.colors;
    const variants = productionQuantitiesItem === null || productionQuantitiesItem === void 0 ? void 0 : productionQuantitiesItem.variants;
    const [selectedVariants, setSelectedVariants] = useState([]);
    const [showErrors, setShowErrors] = useState(false);
    // Target isActive per size being toggled; its loader stays until refetched data confirms it.
    const [pendingSizes, setPendingSizes] = useState({});
    const stepValidationRequestId = useSelector(selectStepValidationRequestId);
    useEffect(() => {
        if (stepValidationRequestId > 0) {
            setShowErrors(true);
        }
    }, [stepValidationRequestId]);
    useEffect(() => {
        dispatch(fetchSizings());
        if (productionQuantitiesStatus.fetch === 'idle' || productionQuantitiesRefetchFlags.fetch) {
            dispatch(fetchSpecificationProductionQuantities({ specificationId: specificationDetails.id }));
        }
    }, [
        dispatch,
        specificationDetails.id,
        productionQuantitiesStatus.fetch,
        productionQuantitiesRefetchFlags.fetch,
    ]);
    useEffect(() => {
        if (productionQuantitiesStatus.fetch === 'failed') {
            setPendingSizes({});
            return;
        }
        setPendingSizes((pending) => {
            const unconfirmed = Object.fromEntries(Object.entries(pending).filter(([id, isActive]) => !(sizes === null || sizes === void 0 ? void 0 : sizes.some((size) => size.id === id && size.isActive === isActive))));
            return Object.keys(unconfirmed).length === Object.keys(pending).length
                ? pending
                : unconfirmed;
        });
    }, [sizes, productionQuantitiesStatus.fetch]);
    const handleSizeActivation = (id, isActive) => {
        setPendingSizes((pending) => (Object.assign(Object.assign({}, pending), { [id]: !isActive })));
        const action = isActive
            ? deactivateSize({ specificationSizeId: id })
            : activateSize({ specificationSizeId: id });
        dispatch(action)
            .unwrap()
            .catch((errorMessage) => {
            setPendingSizes((_a) => {
                var _b = id, _ = _a[_b], pending = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
                return pending;
            });
            dispatch(addToast({
                theme: 'ghost',
                color: 'negative',
                title: t('common:error'),
                description: t(`exceptions:${exceptionMessageToKey(errorMessage)}`, {
                    defaultValue: errorMessage,
                }),
            }));
        });
    };
    const sortVariants = (sizes, variantsRow) => {
        const idOrderMap = new Map();
        sizes.forEach((item, index) => {
            idOrderMap.set(item.id, index);
        });
        return variantsRow.sort((a, b) => {
            var _a, _b;
            const indexA = (_a = idOrderMap.get(a.size.id)) !== null && _a !== void 0 ? _a : Infinity;
            const indexB = (_b = idOrderMap.get(b.size.id)) !== null && _b !== void 0 ? _b : Infinity;
            return indexA - indexB;
        });
    };
    const [debouncedUpdateModelVariants, savingVariantKeys] = useDebouncedCallbackPerKey((variantId, value) => {
        dispatch(updateModelVariants({ variantId, value }))
            .unwrap()
            .then(() => {
            dispatch(fetchSpecificationProblems({ specificationId: specificationDetails.id }));
        });
    }, 1000);
    const handleSizeStandardChange = (specificationId, sizeStandardId) => {
        dispatch(changeSizeStandard({ specificationId, sizeStandardId }));
    };
    const handleSelectVariant = (id) => selectedVariants.includes(id)
        ? setSelectedVariants(selectedVariants.filter((variantId) => variantId !== id))
        : setSelectedVariants([...selectedVariants, id]);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        dispatch(updateStoreModelVariants({ variantId: name, value }));
        debouncedUpdateModelVariants(name, value);
    };
    if (productionQuantitiesStatus.fetch === 'idle' || productionQuantitiesItem === null) {
        return React.createElement(Loader, null);
    }
    // sizeStandard is nullable in the schema - a specification can exist before one is picked.
    const sizeStandardLabel = sizeStandard ? t(sizeStandard.name) : t('size_standard_not_selected');
    return (React.createElement(Grid, { gap: 24 },
        React.createElement(Header, { size: "m", aside: specificationDetails.state === 'PRODUCTION' ? (React.createElement("div", null,
                React.createElement(Button, { theme: "border", rightIcon: React.createElement(ChevronDownSvg, null), disabled: true },
                    t('size_standard'),
                    ": ",
                    sizeStandardLabel))) : (React.createElement(Dropdown, { position: "bottomLeft", trigger: React.createElement(Button, { theme: "border", rightIcon: React.createElement(ChevronDownSvg, null) },
                    t('size_standard'),
                    ": ",
                    sizeStandardLabel) }, sizings.map((sizing) => {
                const isSelected = (sizeStandard === null || sizeStandard === void 0 ? void 0 : sizeStandard.id) === sizing.id;
                return (React.createElement(Button, { key: sizing.id, align: "left", theme: isSelected ? 'fill' : 'ghost', leftIcon: isSelected ? React.createElement(CheckIcon, null) : undefined, onClick: () => handleSizeStandardChange(specificationDetails.id, sizing.id) },
                    t(sizing.name),
                    sizing.isDefault && ` (${t('default_size_standard_current').toLowerCase()})`));
            }))) }, t('production_quantities')),
        sizeStandard === null ? (React.createElement(EmptyState, { header: t('no_size_standard_header'), subheader: t('no_size_standard_subheader') })) : (React.createElement(Grid, { gap: 0 },
            React.createElement(TableGrid, { columns: [
                    // Fits both footer link buttons ("add version" + "remove selected") in PL and EN.
                    ['320px', '1fr'],
                    ...sizes
                        .filter((size) => size.isActive)
                        .map(() => ['80px', '150px']),
                    ['40px', '40px'],
                    ['80px', '150px'],
                ] },
                React.createElement(Head, null,
                    React.createElement(Row, { head: true },
                        React.createElement(Cell, null, t('common:color_version')),
                        sizes.map((size) => {
                            var _a;
                            return size.isActive && (React.createElement(Cell, { key: size.id }, (_a = size.sizeName) === null || _a === void 0 ? void 0 :
                                _a.name,
                                ' ',
                                specificationDetails.state !== 'PRODUCTION' && (React.createElement("aside", null,
                                    React.createElement(Button, { size: "xs", shape: "rounded", leftIcon: React.createElement(MinusIcon, null), onClick: () => handleSizeActivation(size.id, true), loading: size.id in pendingSizes })))));
                        }),
                        React.createElement(Cell, null,
                            React.createElement(Dropdown, { closeOnClick: false, trigger: React.createElement(Button, { size: "xs", shape: "rounded", leftIcon: React.createElement(PlusSvg, null), disabled: specificationDetails.state === 'PRODUCTION' }) },
                                React.createElement(Grid, { vp: 8, hp: 8, gap: 12 },
                                    React.createElement(Caption, { size: "s", color: "subtle" },
                                        t('selected_sizes'),
                                        ":"),
                                    sizes.map((size) => size && (React.createElement(Checkbox, { key: size.id, label: size.sizeName.name, value: size.isActive, onClick: () => handleSizeActivation(size.id, size.isActive), loading: size.id in pendingSizes })))))),
                        React.createElement(Cell, null, t('common:summary')))),
                React.createElement(Body, null, colors.map((color) => {
                    return (React.createElement(Row, { key: color.id },
                        React.createElement(Cell, null, specificationDetails.state !== 'PRODUCTION' ? (React.createElement("span", { style: { display: 'flex', gap: '6px' } },
                            React.createElement(Checkbox, { label: color.name, value: selectedVariants.includes(color.id), onClick: () => handleSelectVariant(color.id) }),
                            specificationDetails.state === 'DRAFT' && (React.createElement(Button, { size: "xs", shape: "rounded", theme: "ghost", leftIcon: React.createElement(EditSvg, null), onClick: () => dispatch(openModal({
                                    name: 'colorEditor',
                                    data: { id: color.id, name: color.name },
                                })) })))) : (React.createElement("p", null, color.name))),
                        sortVariants(sizes, variants.filter((variant) => variant.color.id === color.id)).map((variant) => {
                            return (variant.isActive && (React.createElement(Cell, { key: variant.id },
                                React.createElement(InputNumber, { name: variant.id, value: variant.quantity, onChange: handleInputChange, loading: savingVariantKeys.has(variant.id), suffix: "szt.", disabled: specificationDetails.state === 'PRODUCTION', error: showErrors && Number(variant.quantity) === 0 }))));
                        }),
                        React.createElement(Cell, null),
                        React.createElement(Cell, null,
                            sumBy(variants.filter((variant) => variant.isActive && variant.color.id === color.id), (item) => Number(item.quantity)),
                            ' ',
                            t('pcs_shortcut'))));
                })),
                React.createElement(Footer, null,
                    React.createElement(Row, { foot: true },
                        React.createElement(Cell, null,
                            React.createElement("div", { style: { display: 'flex', gap: '6px' } },
                                React.createElement(LinkButton, { leftIcon: React.createElement(PlusSvg, null), onClick: () => dispatch(openModal({ name: 'colorCreator' })), disabled: specificationDetails.state === 'PRODUCTION' }, t('add_version')),
                                selectedVariants.length > 0 && (React.createElement(LinkButton, { color: "danger", leftIcon: React.createElement(TrashSvg, null), onClick: () => dispatch(removeModelVariants({ colorIds: selectedVariants }))
                                        .unwrap()
                                        .then(() => setSelectedVariants([]))
                                        .catch((errorMessage) => {
                                        dispatch(addToast({
                                            theme: 'ghost',
                                            color: 'negative',
                                            title: t('common:error'),
                                            description: t(`exceptions:${exceptionMessageToKey(errorMessage)}`, {
                                                defaultValue: errorMessage,
                                            }),
                                        }));
                                    }), loading: productionQuantitiesStatus.removeColor === 'loading' }, t('remove_color_versions'))))),
                        sizes.map((size) => size.isActive && (React.createElement(Cell, { key: size.id },
                            sumBy(variants.filter((variant) => variant.size.id === size.id), (item) => Number(item.quantity)) || 0,
                            ' ',
                            t('pcs_shortcut')))),
                        React.createElement(Cell, null),
                        React.createElement(Cell, null,
                            sumBy(variants.filter((variant) => variant.isActive), (item) => Number(item.quantity)),
                            ' ',
                            t('pcs_shortcut')))))))));
}
export default ProductionQuantities;
